import * as Actions from '../actions/matterStageActions';
import MatterStage from '../../entities/matterStage';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type MatterStageAction = {
    type: string;
    matterStage: MatterStage | null; 
    matterStageId: string | MatterStage; 
    partial: boolean; 
    lastFetch: number; 
    matterStages: MatterStage[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<MatterStage> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {MatterStage[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<MatterStage> | null, firmId: string, userId: string, updates?: MatterStage[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'matterStage', 'matterStages', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ matterStageReducer: backup write for 'matterStages' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ matterStageReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<MatterStage>} data
 * @param {MatterStage[]} [updates]
 * @returns {ArrayMap<MatterStage>}
 */
function updateStorage(data: ArrayMap<MatterStage>, updates?: MatterStage[]): ArrayMap<MatterStage> {
	if (logging) console.debug('⮂ matterStageReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {MatterStage[]} [updates]
 * @returns {ArrayMap<MatterStage>}
 */
function updateStorageNoReturn(updates?: MatterStage[]): void {
	if (logging) console.debug('⮂ matterStageReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {MatterStage[]} [data]
 * @returns {ArrayMap<MatterStage>}
 */
function createState(data?: MatterStage[] | ArrayMap<MatterStage>): ArrayMap<MatterStage> {
	if (!data) return ArrayMapRegistry.clone<MatterStage>('matterStage', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<MatterStage>('matterStage', data.map(e => e));
    else return ArrayMapRegistry.clone<MatterStage>('matterStage', data);
}

// Whether deleted matterStages keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<MatterStage>} data
 * @returns {Array<MatterStage | MatterStageDeletionRecord>}
 */
function backupRows(data: ArrayMap<MatterStage>): Array<MatterStage | MatterStageDeletionRecord> {
	const records: MatterStageDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({matterStageId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a matterStage was deleted at a
 * version, never a live row. The storage worker writes one for a deleted matterStage whose type does
 * not keep deleted rows. OWA-68
 */
type MatterStageDeletionRecord = {matterStageId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is MatterStageDeletionRecord => (o as MatterStageDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a matterStage to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<MatterStage>} st
 * @param {MatterStage | MatterStageDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<MatterStage>, incoming: MatterStage | MatterStageDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.matterStageId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as MatterStage);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<MatterStage>} from
 * @returns {ArrayMap<MatterStage>}
 */
function carryOver(from: ArrayMap<MatterStage>): ArrayMap<MatterStage> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function matterStages(state: ArrayMap<MatterStage>, action: MatterStageAction): ArrayMap<MatterStage> {
	if (!state) state = createState();

	let st: ArrayMap<MatterStage>;

	switch (action.type) {
		// action.matterStage - the new value of matterStage with id action.id
		// action.matterStageId - id of the target, used if new value is null
		case Actions.MATTERSTAGE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ matterStageReducer: FETCH_ONE_SUCCESS', action);

			if (action.matterStage === null) return state;

			// Only for a matterStage this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.matterStageId) || state.deletedVersion(action.matterStage.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.matterStage)) return state;

				updateStorageNoReturn([action.matterStage]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.matterStages
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.matterStages
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.matterStages
			 !partial, !stateEmpty, fetchEmpty	-> D return matterStages in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge matterStages in state newer than those in action.matterStages
		 */
		case Actions.MATTERSTAGE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.matterStages.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<MatterStage | MatterStageDeletionRecord> = action.matterStages.map(matterStage => isDeletionRecord(matterStage) ? matterStage : new MatterStage({...matterStage, persisted: true}));
			const fetchedIds = new Set(fetched.map(matterStage => isDeletionRecord(matterStage) ? matterStage.matterStageId : matterStage.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<MatterStage>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ matterStageReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} matterStages with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ matterStageReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(matterStage => applyVersion(st, matterStage));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ matterStageReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<MatterStage> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: MatterStage[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(matterStage => {
					if (applyVersion(st, matterStage)) toUpdate.push(matterStage as MatterStage);
					else if (logging) console.debug('⮂ matterStageReducer: partialMerge: rejected older version of matterStage');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ matterStageReducer: empty: matterStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ matterStageReducer: fullMerge: matterStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(matterStage => applyVersion(st, matterStage));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.MATTERSTAGE_FETCH_ONE_FAILURE:
			console.error('MATTERSTAGE_FETCH_ONE_FAILURE', action.matterStageId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTERSTAGE_FETCH_FAILURE:
			console.error('MATTERSTAGE_FETCH_FAILURE', action.matterStageId, action.err);

			// No update to storage required
			return state;

		case Actions.MATTERSTAGE_RESTORE: {
		    let matterStage = new MatterStage({...action.matterStage});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, matterStage)) return state;

            return updateStorage(st, [matterStage]);
        }
            
		case Actions.MATTERSTAGE_SAVE: {
		    // console.debug('⮂ MATTERSTAGE_SAVE reducer', action.matterStage?.id);
		    
			// clone the matterStage
			let matterStage = new MatterStage({...action.matterStage});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new matterStage in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, matterStage)) return state;

			return updateStorage(st, [matterStage]);
		}

		case Actions.MATTERSTAGE_SAVE_MANY: {
		    if (logging) console.debug('⮂ MATTERSTAGE_SAVE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));
		    
			// clone the matterStages
			let toProcess = action.matterStages.map(matterStage => new MatterStage(matterStage));

			st = state.clone();
			// state.clear();

			const updates: MatterStage[] = [];

			toProcess.forEach(matterStage => {
				// Add or update the new matterStage in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, matterStage)) updates.push(matterStage);
				else if (logging) console.debug('⮂ MATTERSTAGE_SAVE_MANY: rejected older version of matterStage');
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTERSTAGE_REMOVE: {
			// clone the matterStage; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const matterStage = new MatterStage({...action.matterStage, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, matterStage)) return state;

			return updateStorage(st, [matterStage]);
		}

		case Actions.MATTERSTAGE_RESTORE_FAILURE:
		case Actions.MATTERSTAGE_SAVE_FAILURE: {
		    console.warn('MATTERSTAGE_SAVE_FAILURE reducer', action.matterStage?.id);
		    if (!action.matterStage?.id) {
				console.warn('MATTERSTAGE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const matterStage = ensure(MatterStage, action.matterStage);
			if (!matterStage) {
				console.error('Could not find matterStage after call to save');
				return state;
			}

			st = state.clone();

			const _matterStageRef = state.get(matterStage.id);

			if (_matterStageRef && (_matterStageRef.addedOn === matterStage.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _matterStage = new MatterStage({..._matterStageRef});
				_matterStage.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_matterStage.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('matterStage', matterStage.addedOn, {fatalError: _matterStage.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ matterStageReducer: failed to save matterStage with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('matterStage', _matterStage).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected matterStage: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected matterStage: ' + err.message));
						}
						else console.debug('⮂ matterStageReducer: saved matterStage with fatalError info');
					}).catch(err => console.error(`⮂ matterStageReducer: failed to save matterStage with fatalError info: ${err.message}`));

				st.set(_matterStage);
			}

			return updateStorage(st);
		}

		case Actions.MATTERSTAGE_SAVE_MANY_FAILURE: {
		    console.warn('MATTERSTAGE_SAVE_MANY_FAILURE reducer', action.matterStages.map(matterStage => matterStage.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.MATTERSTAGE_RESTORE_SUCCESS:
		case Actions.MATTERSTAGE_SAVE_SUCCESS: {
			if (!action.matterStage?.id) {
				console.warn('MATTERSTAGE_SAVE_SUCCESS reducer');
                return state;
			}

			const matterStageRef = state.get(action.matterStage?.id);
			if (!matterStageRef) {
				console.warn('Could not find matterStage after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.matterStage)) return updateStorage(st, [action.matterStage]);

			if (matterStageRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const matterStage = new MatterStage({...matterStageRef, persisted: true});
			st.set(matterStage);

			return updateStorage(st, [matterStage]);
		}

		case Actions.MATTERSTAGE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ MATTERSTAGE_SAVE_MANY_SUCCESS reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const matterStages: MatterStage[] = [];

			action.matterStages.forEach(e => {
				const matterStageRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!matterStageRef) console.warn('Could not find matterStage after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) matterStages.push(e);
				else if (!matterStageRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const matterStage = new MatterStage({...matterStageRef, persisted: true});
					st.set(matterStage);
					matterStages.push(matterStage);
				}
			});

			return updateStorage(st, matterStages);
		}

		case Actions.MATTERSTAGE_REMOVE_FAILURE: {
		    console.warn('MATTERSTAGE_REMOVE_FAILURE action', action.matterStage?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.MATTERSTAGE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.MATTERSTAGE_RESTORE_MANY: {
			if (logging) console.debug('⮂ MATTERSTAGE_RESTORE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			const toProcess = action.matterStages.map(matterStage => new MatterStage({...matterStage}));
			st = state.clone();

			const updates: MatterStage[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(matterStage => {
				if (applyVersion(st, matterStage)) updates.push(matterStage);
				else if (logging) console.debug('⮂ MATTERSTAGE_RESTORE_MANY: rejected older version of matterStage');
			});

			return updateStorage(st, updates);
		}

		case Actions.MATTERSTAGE_REMOVE_MANY: {
			if (logging) console.debug('⮂ MATTERSTAGE_REMOVE_MANY reducer', action.matterStages.map(matterStage => matterStage.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.matterStages.map(matterStage => new MatterStage({...matterStage, status: 'D'}));
			st = state.clone();

			const updates: MatterStage[] = [];

			toProcess.forEach(matterStage => {
				if (applyVersion(st, matterStage)) updates.push(matterStage);
				else if (logging) console.debug('⮂ MATTERSTAGE_REMOVE_MANY: rejected older version of matterStage');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default matterStages;